import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Skeleton from 'react-loading-skeleton';

import { InfoCircledIcon } from '@radix-ui/react-icons';
import { Flex, Text, Tooltip } from '@raystack/apsara';
import * as _ from 'lodash';
import useSWR from 'swr';

import { getImageLayers } from '@src/components/Custom/ModelCommonComponents/CDCommonComponents/CompareMode/ImageLayersList';
import {
  ChangeDetectionCommon,
  getDateForAsset,
  getURLForChildClassesCategorical
} from '@src/components/Custom/ModelCommonComponents/ChangeDetectionCommon';
import { InsightsOutputHeader } from '@src/containers/Viewer/subComponents/Insights/Header';
import {
  useAoiFloatingComponentsDispatch,
  useMapViewSelector
} from '@src/contexts';
import { For } from '@src/hoc';
import { useGetIndiceById, useGetVisualizationListByAoiId } from '@src/hooks';
import { useGetBlockDetails } from '@src/hooks/useBlocksHooks';
import { setInsightOutputLayer } from '@src/utils';
import { Indices } from '@src/utils/IndiceUtils';
import { GetInferencesForChangeDetection } from '@src/utils/apis/inferenceApis';
import { COMPATIBLE_VIZ_CHANGE_DETECTION } from '@src/utils/constants/ATLAS_CONSTANTS';
import {
  CHANGE_DETECTION_MODEL_TOOLTIP_INFO,
  CHANGE_DETECTION_VIEW_MODES,
  COLOR_LEGEND_MAP_RASTER_NAME,
  MODELS_ALLOWED_CHANGE_DETECTION
} from '@src/utils/constants/MODE_INFERENCE_DEFAULTS';
import { INPUT_FORMATS } from '@src/utils/constants/WORKFLOW_CONSTANTS';
import { getUSTimeFormat } from '@src/utils/dateUtils';
import { fetchJsonDataCD } from '@src/utils/helpers/WorkflowHelpers';
import { h2R } from '@src/utils/hexToRgb';
import {
  getCogURLForOutput,
  getRasterVisualisationColorLegends
} from '@src/utils/workFlowUtils';

export const IndiceDict = Indices.reduce((acc, curr) => {
  acc[curr.id] = { ...curr };
  return acc;
}, {});

export const getPresetIndiceTitle = indice_id => {
  const preset = Indices.find(i => i.id === indice_id);

  if (preset) {
    return preset.name;
  }

  // if not found in Indices constants, then it will return indice_id itself,
  // which will always be a custom indice user input name
  return indice_id;
};

export default function ChangeDetectionOutput({
  activeInsight,
  activeAoi,
  format
}) {
  const mapInstance = useMapViewSelector(store => store.mapInstance);

  const dispatch = useAoiFloatingComponentsDispatch(store => store.dispatch);

  const [loader, setLoader] = useState(false);
  const [graphData, setGraphData] = useState({});
  const [viewMode, setViewMode] = useState(() =>
    !_.isEmpty(activeInsight)
      ? activeInsight.viewMode
      : CHANGE_DETECTION_VIEW_MODES.deltaRaster
  );
  // for categorical mode
  const [childClasses, setChildClasses] = useState({});

  // handle image layers for compare mode
  const [imageLayers, setImageLayers] = useState([]);

  // track if layers have been updated for current compare mode session
  const isLayersUpdatedRef = useRef(false);

  const inputs = useMemo(
    () => activeInsight?.input ?? [],
    [activeInsight?.input]
  );
  const outputs = useMemo(
    () => activeInsight?.output ?? [],
    [activeInsight?.output]
  );

  // insight metadata
  const insight_metadata = activeInsight?.metadata || {};
  const { asset_type = '', date_comp = '', date_ref = '' } = insight_metadata;

  // get model metadata information
  const model_metadata =
    activeInsight?.input?.find(i => i.name === 'raster_url_ref')?.metadata ||
    {};

  // blockName will be empty string when CD was run on ATLAS assets
  const { 'block-name': blockName = '', version = '' } = model_metadata;

  const {
    data: blockData,
    src: InsightThumbnail,
    isLoading
  } = useGetBlockDetails({
    blockName,
    blockVersion: version,
    onError: () => {}
  });

  const isInsightData = MODELS_ALLOWED_CHANGE_DETECTION.includes(asset_type);

  const handleLayerChange = useCallback(layers => {
    setImageLayers(layers);
  }, []);

  const handleColorLegendDataChange = useCallback(
    (insight, mode) => {
      const rasters = insight?.output.filter(
        i => INPUT_FORMATS.FormatRaster === i.format
      );
      const viewModeRaster = rasters.find(
        i => i.name === COLOR_LEGEND_MAP_RASTER_NAME[mode]
      );

      // viewModeRaster be undefined if viewMode is not categorical or detla
      if (!viewModeRaster) {
        dispatch({ colorMapObject: null });
        return;
      }

      dispatch({
        colorMapObject: getRasterVisualisationColorLegends(viewModeRaster)
      });
    },
    [dispatch]
  );

  const { data: insights, isLoading: loadingLayers } = useSWR(
    activeAoi.id ? `/${activeAoi.id}/inferences-change-detection` : null,
    () => GetInferencesForChangeDetection([activeAoi.id]),
    {
      refreshInterval: 10000,
      onError: error => {
        console.log(error);
      },
      onErrorRetry: error => {
        if (error.status === 404) return;
      }
    }
  );

  const { visualizationList, isVisualizationListByAoiIdLoading } =
    useGetVisualizationListByAoiId({ aoi_id: activeAoi.id ?? null });

  const { indice, isIndiceByIdLoading } = useGetIndiceById({
    ids: visualizationList.map(d => d.index_id)
  });

  // TODO: NOTE -> THIS IS NOT A PROPER SOLUTION ANYMORE. WE CAN'T COMPARE USING VISUALIZATION NAME.
  // THIS IS DONE JUST TO MAKE SURE THINGS WORK SINCE THIS IS THE CURRENT SOLUTION.
  // WE NEED TO THINK THROUGH A SOLUTION TO GET VISUALIZATION index_id FROM dexter.
  const visualisations = useMemo(() => {
    if (indice.length === 0 || visualizationList.length === 0) return {};

    return visualizationList
      .filter(i => COMPATIBLE_VIZ_CHANGE_DETECTION.includes(i.expression_type))
      .reduce((acc, curr) => {
        const index = indice.find(i => i.id === curr.index_id);
        acc[curr.index_name] = curr.assets.map(d => ({ ...d, ...index }));
        return acc;
      }, {});
  }, [visualizationList, indice]);

  const getBlockTitle = useCallback(
    (blockData, blockName) => {
      const title = blockData?.spec?.title || blockName;
      return isLoading ? <Skeleton width='50px' /> : title;
    },
    [isLoading]
  );

  const renderTileLayer = useCallback(
    (url, opacity) => {
      if (!mapInstance) return;

      setInsightOutputLayer({
        url,
        mapInstance,
        insight: activeInsight,
        id: url,
        aoi: activeAoi,
        opacity
      });
    },
    [mapInstance, activeAoi, activeInsight]
  );

  const renderChangeDetectionRasterDeltaRasterOutput = useCallback(() => {
    const delta_output = outputs.find(i => i.name === 'delta_raster');
    if (!delta_output) return;

    const url = getCogURLForOutput(delta_output, format);
    if (!url) return;

    renderTileLayer(url);
  }, [format, outputs, renderTileLayer]);

  const renderChangeDetectionRasterCategoricalOutput = useCallback(() => {
    const categorical_raster = outputs.find(
      i => i.name === 'categorical_raster'
    );
    if (!categorical_raster || _.isEmpty(childClasses)) return;

    const url = getURLForChildClassesCategorical(
      categorical_raster?.value ?? '',
      childClasses,
      format
    );
    if (!url) return;

    renderTileLayer(url);
  }, [childClasses, format, outputs, renderTileLayer]);

  const renderChangeDetectionRasterCompareOutput = useCallback(() => {
    const layers = imageLayers
      .filter(d => d.visible && d.url.length > 0)
      .reverse();

    for (const layer of layers) {
      renderTileLayer(layer.url, layer.opacity[0]);
    }
  }, [imageLayers, renderTileLayer]);

  const renderVectorClippings = useCallback(() => {
    const vectorClippings = inputs.find(i => i.name === 'vector_clip')?.value;

    if (!mapInstance || _.isEmpty(vectorClippings)) {
      return;
    }

    const vectors = JSON.parse(vectorClippings);

    const computedStyles = window.getComputedStyle(document.body);
    const rsColorForegroundAccentPrimary = computedStyles.getPropertyValue(
      '--rs-color-foreground-accent-primary'
    );

    mapInstance.addGeoJSONLayer({
      data: vectors,
      options: { id: `clippings-geojson` },
      getFillColor: () => {
        const rgb = h2R(rsColorForegroundAccentPrimary);
        return [rgb.r, rgb.g, rgb.b, 25];
      },
      getLineColor: () => {
        const rgb = h2R(rsColorForegroundAccentPrimary);
        return [rgb.r, rgb.g, rgb.b, 255];
      }
    });
  }, [mapInstance, inputs]);

  const renderChangeDetectionRasterOutput = useCallback(
    mode => {
      if (!mapInstance) return;

      mapInstance.removeAllLayer();

      if (mode === CHANGE_DETECTION_VIEW_MODES.deltaRaster) {
        renderChangeDetectionRasterDeltaRasterOutput();
      }

      if (mode === CHANGE_DETECTION_VIEW_MODES.categorical) {
        renderChangeDetectionRasterCategoricalOutput();
      }

      if (mode === CHANGE_DETECTION_VIEW_MODES.compare) {
        renderChangeDetectionRasterCompareOutput();
      }

      renderVectorClippings();
    },
    [
      renderChangeDetectionRasterDeltaRasterOutput,
      renderChangeDetectionRasterCategoricalOutput,
      renderChangeDetectionRasterCompareOutput,
      renderVectorClippings,
      mapInstance
    ]
  );

  useEffect(() => {
    if (!activeInsight || _.isEmpty(activeInsight) || !mapInstance) return;

    // fetch graph data
    fetchJsonDataCD(activeInsight.output, setGraphData, setLoader, activeAoi);
  }, [activeInsight, mapInstance, activeAoi]);

  const handleViewChange = value => {
    setViewMode(value);
    isLayersUpdatedRef.current = false;
  };

  const isLayersLoading =
    loadingLayers || isVisualizationListByAoiIdLoading || isIndiceByIdLoading;

  useEffect(() => {
    if (!activeInsight || _.isEmpty(activeInsight)) return;

    renderChangeDetectionRasterOutput(viewMode);
    handleColorLegendDataChange(activeInsight, viewMode);
  }, [
    viewMode,
    activeInsight,
    renderChangeDetectionRasterOutput,
    handleColorLegendDataChange
  ]);

  useEffect(() => {
    if (isLayersLoading) return;

    if (
      viewMode === CHANGE_DETECTION_VIEW_MODES.compare &&
      !isLayersUpdatedRef.current
    ) {
      handleLayerChange(
        getImageLayers({
          inputs,
          outputs,
          date_ref,
          date_comp,
          isModel: isInsightData,
          asset_type,
          format,
          activeAoi,
          insights,
          visualisations
        })
      );

      isLayersUpdatedRef.current = true;
    }
  }, [
    isLayersLoading,
    viewMode,
    handleLayerChange,
    inputs,
    outputs,
    date_ref,
    date_comp,
    isInsightData,
    asset_type,
    format,
    activeAoi,
    insights,
    visualisations
  ]);

  const asset_ref = useMemo(
    () =>
      isInsightData
        ? {
            name: getBlockTitle(blockData, blockName),
            description: getDateForAsset(blockName, date_ref),
            src: InsightThumbnail,
            preview_url: getCogURLForOutput(
              activeInsight.output.find(d => d.name === 'raster_ref') ?? ''
            )
          }
        : {
            name: getPresetIndiceTitle(asset_type),
            description: `${getUSTimeFormat(date_ref)}`,
            src: IndiceDict[asset_type]?.icon ?? '',
            preview_url: getCogURLForOutput(
              activeInsight.output.find(d => d.name === 'raster_ref')
            )
          },
    [
      isInsightData,
      getBlockTitle,
      blockData,
      blockName,
      date_ref,
      InsightThumbnail,
      activeInsight,
      asset_type
    ]
  );

  const asset_comp = useMemo(
    () =>
      isInsightData
        ? {
            name: getBlockTitle(blockData, blockName),
            description: getDateForAsset(blockName, date_comp),
            src: InsightThumbnail,
            preview_url: getCogURLForOutput(
              activeInsight.output.find(d => d.name === 'raster_comp') ?? ''
            )
          }
        : {
            name: getPresetIndiceTitle(asset_type),
            description: `${getUSTimeFormat(date_comp)}`,
            src: IndiceDict[asset_type]?.icon ?? '',
            preview_url: getCogURLForOutput(
              activeInsight.output.find(d => d.name === 'raster_comp')
            )
          },
    [
      isInsightData,
      getBlockTitle,
      blockData,
      blockName,
      date_comp,
      InsightThumbnail,
      activeInsight,
      asset_type
    ]
  );

  return (
    <>
      <InsightsOutputHeader
        activeInsight={activeInsight}
        titleTailingContent={
          <Tooltip
            side='bottom'
            message={
              <Flex direction='column' gap='medium'>
                <For each={CHANGE_DETECTION_MODEL_TOOLTIP_INFO}>
                  {info => <Text key={info.id}>{info.description}</Text>}
                </For>
              </Flex>
            }
          >
            <InfoCircledIcon />
          </Tooltip>
        }
      />

      <ChangeDetectionCommon
        graphData={graphData}
        viewMode={viewMode}
        handleViewChange={handleViewChange}
        asset_ref={asset_ref}
        asset_comp={asset_comp}
        loader={loader}
        layers={imageLayers}
        onLayersChange={handleLayerChange}
        setChildClasses={setChildClasses}
        layersLoading={isLayersLoading}
      />
    </>
  );
}
